import * as React from "react"
import {Box, Grid, Typography, Chip} from "@mui/material";
import {AppLink} from "@ui-seeker/app";
import {ButtonLoadingSeeker, IconSeeker, ImageSeeker} from "@ui-seeker/core";
import {formatDate, formatPrice} from "@ui-seeker/utils";

import {productsMatcherImageComparatorModalData} from "../@signals";
import {useProxiedImage} from "../../@hooks";


// Color del badge según el score
const getScoreColor = (distance) => {
	const score = parseFloat(distance)
	if (isNaN(score)) return 'default'
	if (score >= 0.9) return 'success'
	if (score >= 0.7) return 'primary'
	if (score >= 0.5) return 'warning'
	return 'error'
}

export default function ProductsMatcherTableRow({ item, onValidate, onEdit, onNotFound, isValidating }) {
	const { ownProductID, ownTitle, ownVariant, ownReference, ean, brand, retailer, targetRetailer, aiCandidate } = item
	const hasCandidate = aiCandidate != null
	const { decodedURL: candidateThumbnail, imageResourceURL: candidateImage } = useProxiedImage(aiCandidate?.thumbnail)
	const { decodedURL: ownThumbnail, imageResourceURL: ownImage } = useProxiedImage(item.ownThumbnail)

	const getCandidateName = () => {
		if (!aiCandidate) return 'No Candidate'
		return [aiCandidate.productTitle, aiCandidate.variant].filter(Boolean).join(' ')
	}
	const candidateName = getCandidateName()

	const openCompare = () => productsMatcherImageComparatorModalData.value = {
		title: `${ownTitle} vs ${candidateName}`,
		urlLeft: item.ownThumbnail,
		urlRight: candidateThumbnail,
		skuName: ownTitle,
		candidateName: candidateName,
		targetIndex: 0
	}

	return <Grid container
		spacing={2}
		p={2}
		mb={2}
		sx={{
			border: '2px solid',
			borderColor: hasCandidate ? 'divider' : 'warning.light',
			borderRadius: 1,
			backgroundColor: 'background.paper'
		}}
	>
		{/* Own product - Left */}
		<Grid item xs={6}>
			<Box display="flex" gap={2} alignItems="flex-start">
				<ImageSeeker
					useSpinner
					lazyLoad={false}
					src={ownImage}
					sx={{ border: '1px solid', borderColor: 'divider', width: 120, height: 'auto', maxHeight: 150, cursor: 'pointer', borderRadius: 1 }}
					onClick={openCompare}
				/>
				<Box flex={1}>
					<Typography fontWeight="bold" fontSize="0.95rem" mb={0.5}>
						{ownTitle}
					</Typography>
					<Typography fontSize="0.85rem" color="text.secondary">
						<strong>Product ID:</strong> {ownProductID}
					</Typography>
					<Typography fontSize="0.85rem" color="text.secondary">
						<strong>EAN:</strong> {ean || "-"}
					</Typography>
					<Typography fontSize="0.85rem" color="text.secondary">
						<strong>Brand:</strong> {brand || "-"}
					</Typography>
					<Typography fontSize="0.85rem" color="text.secondary">
						<strong>Variant:</strong> {ownVariant || "-"}
					</Typography>
					<Typography fontSize="0.85rem" color="text.secondary">
						<strong>Reference:</strong> {ownReference || "-"}
					</Typography>
				</Box>
			</Box>
		</Grid>

		{/* Competitor candidate - Right */}
		<Grid item xs={6}>
			{hasCandidate ? (
				<Box display="flex" gap={2} alignItems="flex-start">
					<Box position="relative">
						<ImageSeeker
							useSpinner
							lazyLoad={false}
							src={candidateImage}
							sx={{ border: '1px solid', borderColor: 'divider', width: 120, height: 'auto', maxHeight: 150, cursor: 'pointer', borderRadius: 1 }}
							onClick={openCompare}
						/>
						<Box position="absolute" top={-8} right={-8}>
							<Chip
								icon={<IconSeeker icon="sparkles" sx={{ fontSize: "0.7rem !important", color: "inherit !important" }} />}
								label={aiCandidate.AIDistance ? `${Math.round(parseFloat(aiCandidate.AIDistance) * 100)}%` : "N/A"}
								color={getScoreColor(aiCandidate.AIDistance)}
								variant="filled"
								size="small"
								sx={{ fontSize: "0.7rem", height: 20, fontWeight: 'bold' }}
							/>
						</Box>
					</Box>

					<Box flex={1}>
						<Box display="flex" alignItems="center" gap={1} mb={0.5}>
							<AppLink to={aiCandidate.productURL || '#'} target="_blank">
								<Typography display="flex" alignItems="center" gap={0.5} fontWeight="bold" fontSize="0.95rem">
									{aiCandidate.productTitle} {aiCandidate.variant && `${aiCandidate.variant}`}
									<IconSeeker icon="external-link" sx={{ color: 'text.disabled', fontSize: "0.75rem" }} />
								</Typography>
							</AppLink>
							<Chip label={targetRetailer} size="small" color="info" sx={{ fontSize: "0.7rem", height: 20 }} />
						</Box>
						<Typography fontSize="0.85rem" color="text.secondary" mt={0.5}>
							<strong>Product ID:</strong> {aiCandidate.productID}
						</Typography>
						<Typography fontSize="0.85rem" color="text.secondary">
							<strong>Variant:</strong> {aiCandidate.variant || "-"}
						</Typography>
						{aiCandidate.reference && <Typography fontSize="0.85rem" color="text.secondary">
							<strong>Reference:</strong> {aiCandidate.reference}
						</Typography>}
					</Box>

					{/* Action Buttons */}
					<Box display="flex" flexDirection="column" gap={1} justifyContent="flex-end" minWidth={140}>
						<ButtonLoadingSeeker
							color="success" size="small" loading={isValidating} disabled={!hasCandidate}
							startIcon={<IconSeeker icon="badge-check" />}
							onClick={() => onValidate(item)} sx={{ width: '100%' }}
						>
							Validate
						</ButtonLoadingSeeker>
						<ButtonLoadingSeeker
							color="error" size="small" loading={isValidating}
							startIcon={<IconSeeker icon="person-circle-question" />}
							onClick={() => onNotFound(item)} sx={{ width: '100%' }}
						>
							Not Found
						</ButtonLoadingSeeker>
						<ButtonLoadingSeeker
							color="primary" size="small"
							startIcon={<IconSeeker icon="pen-to-square" />}
							onClick={() => onEdit(item)} sx={{ width: '100%' }}
						>
							Edit
						</ButtonLoadingSeeker>
					</Box>
				</Box>
			) : (
				<Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" gap={2}>
					<IconSeeker icon="circle-xmark" sx={{ fontSize: "3rem", color: 'text.disabled' }} />
					<Typography variant="h6" color="text.disabled">No AI Candidate Available</Typography>
					<Box display="flex" gap={1}>
						<ButtonLoadingSeeker
							color="error" size="small" loading={isValidating}
							startIcon={<IconSeeker icon="person-circle-question" />}
							onClick={() => onNotFound(item)}
						>
							Not Found
						</ButtonLoadingSeeker>
						<ButtonLoadingSeeker
							color="primary" size="small"
							startIcon={<IconSeeker icon="pen-to-square" />}
							onClick={() => onEdit(item)}
						>
							Edit
						</ButtonLoadingSeeker>
					</Box>
				</Box>
			)}
		</Grid>
	</Grid>
}
